// Notification preferences — `GET/PUT /v1/notifications/prefs` (spec/16 §3, spec/08 §4.2).
//
// As far as consent goes, which is what F-CMP-06 needs: the marketing consent, read and changed. The
// rest of the preference model — categories against channels, quiet hours, a per-venue mute — is
// F-NTF-01's (#189) and is added to these same two shapes, as new optional fields, when it lands.
//
// Marketing here and `marketingOptIn` on `PATCH /v1/me` are the same consent through two doors: both
// write the same consent rows and the same mirror, so a player can change it wherever they find it.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { NotificationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { MarketingConsentSchema, NotificationChannelSchema } from "../primitives/consent.js";
import { NotificationCategorySchema, NotificationCodeSchema, OptInNotificationCodeSchema } from "../primitives/notification.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, jsonResponse, problemResponse } from "../openapi/responses.js";
export const NotificationPrefsSchema = z
    .object({
    /**
     * The marketing consent (`news`, offers). Off unless the player switched it on. Even when it is
     * on, push stays off and WhatsApp, SMS and email need their own opt-in (spec/16 §3).
     */
    marketing: MarketingConsentSchema,
})
    .openapi('NotificationPrefs', {
    description: "The signed-in person's notification preferences. Categories, channels, quiet hours and venue " +
        'mutes are added by F-NTF-01.',
});
/** Switch marketing on or off. */
export const MarketingChoiceSchema = z
    .object({ optIn: z.boolean() })
    .strict()
    .openapi('MarketingChoice', { description: 'Marketing on or off.' });
export const PutNotificationPrefsSchema = z
    .object({
    marketing: MarketingChoiceSchema,
})
    .strict()
    .openapi('PutNotificationPrefs');
const SIGNED_IN = 'Needs a signed-in user. It is about the caller only.';
export const getNotificationPrefsRoute = createRoute({
    method: 'get',
    path: '/v1/notifications/prefs',
    operationId: 'getNotificationPrefs',
    tags: ['notifications'],
    summary: 'Your notification preferences',
    description: `${SIGNED_IN} Today: whether marketing is on, and since when.`,
    responses: {
        200: jsonResponse(NotificationPrefsSchema, 'The preferences.'),
        401: commonProblemResponses[401],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const putNotificationPrefsRoute = createRoute({
    method: 'put',
    path: '/v1/notifications/prefs',
    operationId: 'putNotificationPrefs',
    tags: ['notifications'],
    summary: 'Change your notification preferences',
    description: `${SIGNED_IN} Switching marketing off stamps \`revokedAt\` on the grant in force and stops marketing at ` +
        'once; switching it on writes a new consent row. Transactional messages are not affected.',
    request: { body: { content: { 'application/json': { schema: PutNotificationPrefsSchema } }, required: true } },
    responses: {
        200: jsonResponse(NotificationPrefsSchema, 'The preferences as they now stand.'),
        400: problemResponse('The request could not be understood, or the player has not accepted a privacy policy to opt in under.', 'validation-failed', 'consent-required'),
        401: commonProblemResponses[401],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// -------------------------------------------------------------------------------------------------
// The in-app inbox (F-NTF-03, spec/16 §2, §6) — the durable record the console bell reads.
//
// Organisation-scoped, because a staff member's notifications belong to the organisation whose event
// raised them (`floor` alerts, `orders`, `payments`, …) and are read through the request transaction
// under row-level security. Every read and write is about the caller's own rows — the handler ands
// `recipient_user_id = <the caller>` onto the scope predicate — so these carry no permission: reading
// your own notifications is authenticated, not permissioned (spec/16 §3, spec/07 §6.3). A player's
// cross-organisation app inbox is a later surface; this is the staff console's.
// -------------------------------------------------------------------------------------------------
export const NotificationSchema = z
    .object({
    id: NotificationIdSchema,
    /** The catalogue id this row was rendered from (spec/16 §4.3). */
    code: NotificationCodeSchema,
    category: NotificationCategorySchema,
    /** The venue the notice is about, when it is about one (a floor alert, an order); else absent. */
    venueId: VenueIdSchema.nullable(),
    title: z.string(),
    body: z.string(),
    /** The named variables the row was rendered with, for a client that re-renders or deep-links. */
    data: z.record(z.string(), z.unknown()),
    /** When the person marked it read, RFC 3339 UTC; `null` while it is unread. */
    readAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
})
    .openapi('Notification', { description: 'One inbox notification (spec/16 §2).' });
export const NotificationListSchema = paginated(NotificationSchema, 'NotificationList');
/** `?status=unread` narrows the inbox to what has not been read; `all` (the default) returns everything. */
export const NotificationListQuerySchema = PageQuerySchema.extend({
    status: z
        .enum(['all', 'unread'])
        .default('all')
        .openapi({ description: 'Whether to return every notification or only the unread ones.' }),
}).openapi('NotificationListQuery');
const NotificationParamsSchema = OrganizationScopeParamsSchema.extend({
    notificationId: NotificationIdSchema,
}).openapi('NotificationParams');
export const listNotificationsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/notifications',
    operationId: 'listNotifications',
    tags: ['notifications'],
    summary: 'Your inbox at this organisation',
    description: 'The signed-in person’s own notifications at this organisation, newest first. No permission: it is ' +
        'about the caller only, and never shows another person’s rows.',
    request: { params: OrganizationScopeParamsSchema, query: NotificationListQuerySchema },
    responses: {
        200: jsonResponse(NotificationListSchema, 'A page of the caller’s notifications.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        404: problemResponse('No such organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const markNotificationReadRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/notifications/{notificationId}/read',
    operationId: 'markNotificationRead',
    tags: ['notifications'],
    summary: 'Mark one notification read',
    description: 'Idempotent: a notification already read is returned unchanged. Only the caller’s own rows.',
    request: { params: NotificationParamsSchema },
    responses: {
        200: jsonResponse(NotificationSchema, 'The notification, now read.'),
        401: commonProblemResponses[401],
        404: problemResponse('No such notification of yours at this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const MarkNotificationsReadResultSchema = z
    .object({ updated: z.int().min(0) })
    .openapi('MarkNotificationsReadResult', { description: 'How many notifications this call marked read.' });
export const markAllNotificationsReadRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/notifications/read',
    operationId: 'markAllNotificationsRead',
    tags: ['notifications'],
    summary: 'Mark every unread notification read',
    description: 'Marks all of the caller’s unread notifications at this organisation read, and says how many.',
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(MarkNotificationsReadResultSchema, 'The count marked read.'),
        401: commonProblemResponses[401],
        404: problemResponse('No such organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// -------------------------------------------------------------------------------------------------
// The preference model (F-NTF-01, spec/16 §3) — categories against channels, quiet hours, venue mutes.
//
// Per person, per organisation. Absent overrides mean the category's default (spec/16 §3): transactional
// categories default on for push and inbox; marketing (`news`) defaults off for push and needs opt-in for
// the external channels. A stored override never turns inbox off for a transactional category — the server
// keeps it on whatever is sent (spec/16 §3), so it is not offered here.
// -------------------------------------------------------------------------------------------------
const QuietHoursTimeSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected a 24-hour local time, HH:MM')
    .openapi({ description: 'A local time of day, HH:MM (24-hour).', example: '22:00' });
export const QuietHoursSchema = z
    .object({
    enabled: z.boolean(),
    /** Local to the recipient (or the venue). Non-urgent messages are held; urgent ones ignore it (spec/16 §3). */
    start: QuietHoursTimeSchema,
    end: QuietHoursTimeSchema,
})
    .openapi('QuietHours', { description: 'Hours in which non-urgent notifications are held (spec/16 §3).' });
export const CategoryChannelPreferenceSchema = z
    .object({
    category: NotificationCategorySchema,
    channel: NotificationChannelSchema,
    enabled: z.boolean(),
})
    .openapi('CategoryChannelPreference', {
    description: 'One override: whether a category may reach the person on one channel.',
});
/**
 * One opt-in notification the person has subscribed to (spec/16 §3, ADR-0044): N-27, the owners' daily summary, for
 * the whole organisation (`venueId: null`) or for one venue. Nothing is subscribed until the person subscribes.
 */
export const NotificationSubscriptionInputSchema = z
    .object({
    code: OptInNotificationCodeSchema,
    /** One venue’s summary; `null` for the organisation’s (every live venue). */
    venueId: VenueIdSchema.nullable(),
})
    .strict()
    .openapi('NotificationSubscriptionInput', { description: 'Subscribe to an opt-in notification, for the organisation or one venue.' });
export const NotificationSubscriptionSchema = z
    .object({
    code: OptInNotificationCodeSchema,
    /** One venue’s summary; `null` for the organisation’s. */
    venueId: VenueIdSchema.nullable(),
    /** When the person subscribed: a summary goes to them for a day that became due after it. */
    since: IsoDateTimeSchema,
})
    .openapi('NotificationSubscription', { description: 'An opt-in notification the person receives, and since when.' });
export const NotificationPreferencesSchema = z
    .object({
    quietHours: QuietHoursSchema,
    /** Per-category, per-channel overrides of the defaults. An absent pair means the default (spec/16 §3). */
    overrides: z.array(CategoryChannelPreferenceSchema),
    /** Venues whose non-transactional messages the person has muted (spec/16 §3). */
    mutedVenueIds: z.array(VenueIdSchema),
    /** The opt-in notifications the person receives (ADR-0044): none until they subscribe. */
    subscriptions: z.array(NotificationSubscriptionSchema),
})
    .openapi('NotificationPreferences', {
    description: 'The signed-in person’s notification preferences at this organisation (spec/16 §3).',
});
export const PutNotificationPreferencesSchema = z
    .object({
    quietHours: QuietHoursSchema.optional(),
    overrides: z.array(CategoryChannelPreferenceSchema).optional(),
    mutedVenueIds: z.array(VenueIdSchema).optional(),
    /**
     * The whole set of opt-in notifications, replacing the stored set. N-27 is the owners’ daily summary: only an
     * owner of the organisation may subscribe (`403` otherwise), and a venue must be one of its live venues.
     */
    subscriptions: z.array(NotificationSubscriptionInputSchema).max(200).optional(),
})
    .strict()
    .openapi('PutNotificationPreferences', {
    description: 'Change some or all of the preferences. Omitted fields are left as they are.',
});
export const getNotificationPreferencesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/notification-preferences',
    operationId: 'getNotificationPreferences',
    tags: ['notifications'],
    summary: 'Your notification preferences at this organisation',
    description: 'Categories against channels, quiet hours and muted venues. About the caller only.',
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(NotificationPreferencesSchema, 'The preferences.'),
        401: commonProblemResponses[401],
        404: problemResponse('No such organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const putNotificationPreferencesRoute = createRoute({
    method: 'put',
    path: '/v1/organizations/{organizationId}/notification-preferences',
    operationId: 'putNotificationPreferences',
    tags: ['notifications'],
    summary: 'Change your notification preferences at this organisation',
    description: 'Sets the quiet hours, the per-category per-channel overrides, the muted venues and the opt-in notifications. A ' +
        'transactional category can never be turned off for inbox (spec/16 §3), so such an override is ignored. Only an ' +
        'owner may subscribe to the daily summary (N-27).',
    request: {
        params: OrganizationScopeParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: PutNotificationPreferencesSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(NotificationPreferencesSchema, 'The preferences as they now stand.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Only an owner of the organisation may subscribe to the daily summary.', 'forbidden'),
        404: problemResponse('No such organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=notifications.js.map